import { ColumnBoxFlexFullWidth } from '../common/boxes';
import { useCallback, useMemo, useState } from 'react';
import { Loader } from '../common/Loader';
import { useQueryParamsState } from '../../hooks/useQueryParamsState';
import { PAGINATION_SIZE_OPTIONS } from '../../core/pagination';
import { IProjectPositionSearchRequest, IProjectPositionSearchResponse } from '../../interfaces/projectPostitionSearch';
import { GET_ALL_POSITIONS_QUERY_KEY, useGetAllPositions } from '../../queryHooks/project/position/useGetAllPositions';
import JobBoardTableWithSearch from './JobBoardTableWithSearch';
import { useSearchParams } from 'react-router-dom';
import PositionDetailsModal from './PositionDetailsModal';
import { ISearchHeaderInputProps } from '../../interfaces/searchHeaderProps';
import { PROJECT_POSITION_STATUS_MAP, ProjectPositionStatus } from '../../enums/projectPositionStatus';
import { AVAILABILITY_TYPE_MAP, AvailabilityType } from '../../enums/AvailabilityType';
import { CURRENCY_CODES } from '../../core/currencies';
import { getStateFromSearchInputProps } from '../../utils/getStateFromSearchInputProps';
import { getProjectPositionFromSearchResponse } from '../../utils/getProjectPositionFromSearchResponse';
import { IBaseProjectPosition } from '../../interfaces/projectPosition';
import PositionEntityModal, {
  IPositionEntityModalProps
} from '../common/project-positions/position-entity-modal/PositionEntityModal';
import { useQueryClient } from '@tanstack/react-query';
import { ProjectOpportunityService } from '../../services/project-opportunity';
import { ADD_APPLICANT_QUERY_PARAM, APPLICANTS_QUERY_PARAM } from '../project-opportunities/project-opportunity-entity/ProjectOpportunityEntityPositions';
import PositionApplicantsModal from '../common/project-positions/position-applicants-modal/PositionApplicantsModal';
import AddApplicantModal from '../common/project-positions/position-applicants-modal/AddApplicantModal';
import { IProjectApplicantRequest } from '../../interfaces/projectApplicantRequest';
import { PROFICIENCY_DESCRIPTIONS, PROFICIENCY_MAP } from '../../interfaces/language';

const initialSearchInputProps: ISearchHeaderInputProps[] = [
  { name: 'id', label: 'ID', sx: { width: 100 } },
  { name: 'clientId', label: 'Client ID', sx: { width: 100 }, isNumber: true },
  { name: 'clientName', label: 'Client Name' },
  { name: 'projectOpportunityId', label: 'Project ID', sx: { width: 100 } },
  { name: 'positionTitle', label: 'Title' },
  {
    name: 'status', label: 'Status',
    select: {
      list: Object.values(ProjectPositionStatus),
      getLabel: (t: ProjectPositionStatus) => PROJECT_POSITION_STATUS_MAP[t],
      getValue: (t) => t
    }
  },
  { name: 'link', label: 'Link' },
  {
    name: 'timezone',
    label: 'Timezone',
    isTimezoneAutocomplete: true
  },
  {
    name: 'availabilityType',
    label: 'Availability Type',
    select: {
      list: Object.values(AvailabilityType),
      getLabel: (t: AvailabilityType) => AVAILABILITY_TYPE_MAP[t],
      getValue: (t) => t
    },
    sx: { minWidth: { mobile: '100%', laptop: 160 } }
  },
  {
    name: 'rateCurrency',
    label: 'Currency',
    select: {
      list: CURRENCY_CODES,
      getLabel: (v) => v,
      getValue: (v) => v
    }
  },
  { name: 'rateAmountFrom', label: 'Amount From', isNumber: true },
  { name: 'rateAmountTo', label: 'Amount To', isNumber: true },
  { name: 'requiredSkills', label: 'Skills' },
  { name: 'languageCode', label: 'Language', isLanguageAutocomplete: true },
  {
    name: 'languageProficiencyLevel',
    label: 'Proficiency Level',
    select: {
      list: Object.values(PROFICIENCY_DESCRIPTIONS),
      getValue: (v: PROFICIENCY_DESCRIPTIONS) => Object.values(PROFICIENCY_DESCRIPTIONS).indexOf(v),
      getLabel: (v: PROFICIENCY_DESCRIPTIONS) => PROFICIENCY_MAP[v],
    },
    sx: { minWidth: { mobile: '100%', laptop: 160 } }
  },
  { name: 'fromDate', label: 'From Date', isDatePicker: true },
  { name: 'toDate', label: 'To Date', isDatePicker: true }
];
const initialSearchState: IProjectPositionSearchRequest = getStateFromSearchInputProps<IProjectPositionSearchRequest>(initialSearchInputProps);
export const POSITION_DETAILS_QUERY_PARAM = 'details';
export const POSITION_ENTITY_QUERY_PARAM = 'position';

const handleAddApplicant = (applicant: IProjectApplicantRequest) => ProjectOpportunityService.addApplicantFreelancer(applicant);


const JobBoard = () => {
  const [searchState, setSearchState] = useState<IProjectPositionSearchRequest>(initialSearchState);
  const [totalCount, setTotalCount] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();

  const { queryParamState, isPrePopulated } = useQueryParamsState({
    state: searchState,
    setState: setSearchState,
    wildcards: ['clientName', 'positionTitle', 'link', 'languageCode', ''],
    arrays: ['requiredSkills'],
    parser: { size: (val: string) => PAGINATION_SIZE_OPTIONS.includes(+val) ? +val : PAGINATION_SIZE_OPTIONS[0] }
  });

  const { data, isLoading } = useGetAllPositions({
    search: queryParamState,
    enabled: !!Object.keys(queryParamState).length,
    onSuccess: (data) => setTotalCount(data.size * data.totalPages - (data.size - data.totalElements))
  });
  const positions = useMemo(() => data?.content || [], [data]);
  const positionDetailsId = useMemo(() => searchParams.get(POSITION_DETAILS_QUERY_PARAM), [searchParams]);
  const positionEntityId = useMemo(() => searchParams.get(POSITION_ENTITY_QUERY_PARAM), [searchParams]);

  const refreshSearch = useCallback(() => queryClient.invalidateQueries([GET_ALL_POSITIONS_QUERY_KEY]), [queryClient]);

  const handleCloseModal = useCallback((key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  }, [searchParams, setSearchParams]);

  const handleDeletePosition = useCallback((search: IProjectPositionSearchResponse) => ProjectOpportunityService.deletePosition({
    positionId: search.id,
    clientId: search.clientId,
    projectOpportunityId: search.projectOpportunityId
  }), []);

  const positionEntityModalProps = useMemo<IPositionEntityModalProps | undefined>(() => {
    const searchResult = !!positions.length && positionEntityId ? positions.find(d => d.id === positionEntityId) : undefined;
    if (!searchResult) {return;}
    return {
      positionEntity: getProjectPositionFromSearchResponse(searchResult),
      onClose: () => handleCloseModal(POSITION_ENTITY_QUERY_PARAM),
      refreshPositions: refreshSearch,
      handleUpdatePosition: (positionId: string, position: IBaseProjectPosition) =>
        ProjectOpportunityService.editPosition({
          clientId: searchResult.clientId,
          positionId,
          projectOpportunityId: searchResult.projectOpportunityId,
          data: position
        }),
      handleAddNewSkillsToPosition: (positionId: string, skills: string[]) => ProjectOpportunityService.addSkillsToPosition({
        clientId: searchResult.clientId,
        positionId,
        projectOpportunityId: searchResult.projectOpportunityId,
        skills
      }),
      handleRemoveSkillsFromPosition: (positionId: string, skills: string[]) => ProjectOpportunityService.deleteSkillsFromPosition({
        clientId: searchResult.clientId,
        positionId,
        projectOpportunityId: searchResult.projectOpportunityId,
        skills
      }),
    };
  }, [positions, positionEntityId, handleCloseModal, refreshSearch]);

  const positionEntityDetails = useMemo(() => positions.find(p => p.id === positionDetailsId), [positions, positionDetailsId]);

  const applicantsQueryParam = useMemo(() => searchParams.get(APPLICANTS_QUERY_PARAM), [searchParams]);
  const applicantsForPosition = useMemo(() => positions.find(pos => pos.id === applicantsQueryParam), [positions, applicantsQueryParam]);

  const addApplicantQueryParam = useMemo(() => searchParams.get(ADD_APPLICANT_QUERY_PARAM), [searchParams]);
  const addApplicantForPosition = useMemo(() => positions.find(pos => pos.id === addApplicantQueryParam), [positions, addApplicantQueryParam]);

  const handleGetApplicants = useCallback((positionId: string) => {
    return ProjectOpportunityService.getPositionApplicants({
      clientId: applicantsForPosition?.clientId!,
      projectOpportunityId: applicantsForPosition?.projectOpportunityId!,
      positionId
    });
  }, [applicantsForPosition]);

  return (
    <ColumnBoxFlexFullWidth>
      {
        isLoading && <Loader/>
      }
      <ColumnBoxFlexFullWidth>
        {
          isPrePopulated &&
          <JobBoardTableWithSearch
            refreshPositions={refreshSearch}
            handleDeletePosition={handleDeletePosition}
            tableData={{
              list: positions,
              pagination: {
                size: searchState.size,
                page: searchState.page,
                setState: setSearchState,
                sizeOptions: PAGINATION_SIZE_OPTIONS,
                totalCount: totalCount
              }
            }}
            searchProps={{
              initialState: initialSearchState,
              params: searchState,
              setParams: setSearchState,
              inputProps: initialSearchInputProps
            }}
          />
        }

      </ColumnBoxFlexFullWidth>
      {
        positionEntityDetails &&
        <PositionDetailsModal onClose={() => handleCloseModal(POSITION_DETAILS_QUERY_PARAM)}
                              position={positionEntityDetails}/>
      }
      {
        positionEntityModalProps && <PositionEntityModal {...positionEntityModalProps}/>
      }
      {
        applicantsForPosition &&
        <PositionApplicantsModal
          onClose={() => handleCloseModal(APPLICANTS_QUERY_PARAM)}
          positionEntity={getProjectPositionFromSearchResponse(applicantsForPosition)}
          handleGetApplicants={handleGetApplicants}
        />
      }
      {
        addApplicantForPosition && handleAddApplicant &&
        <AddApplicantModal
          positionEntity={getProjectPositionFromSearchResponse(addApplicantForPosition)}
          handleAddApplicant={handleAddApplicant}
        />
      }
    </ColumnBoxFlexFullWidth>
  );
};

export default JobBoard;